import * as React from "react";
import { NO_POLICY } from "@authe/fe-commons/attribution";
import { useConfigSnapshot } from "./configSnapshot";
import { EnforcedPolicyContext, type EnforcedPolicyState } from "./enforcedPolicy";

/** The one read of the tenancy's policy. */
function useTenancyEnforcedPolicy(): EnforcedPolicyState {
  const snapshot = useConfigSnapshot();
  const query = { ...snapshot, data: snapshot.data?.policies };
  return React.useMemo<EnforcedPolicyState>(() => {
    // A refetch that fails keeps the last good answer (react-query keeps
    // `data`), so a blip does not unlock what was locked a minute ago.
    if (query.data) {
      return { policy: query.data, status: query.data.readable ? "ready" : "unreadable" };
    }
    if (query.isError) return { policy: { ...NO_POLICY, readable: false }, status: "unreadable" };
    return { policy: NO_POLICY, status: query.fetchStatus === "idle" ? "ready" : "loading" };
  }, [query.data, query.isError, query.fetchStatus]);
}

/** Reads the tenancy's policy and publishes it. Mounted once, signed in. */
export function DashboardEnforcedPolicy({ children }: { children: React.ReactNode }): React.ReactElement {
  const state = useTenancyEnforcedPolicy();
  return <EnforcedPolicyContext.Provider value={state}>{children}</EnforcedPolicyContext.Provider>;
}
